import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './CapturistaDashboard.css';

const CapturistaDashboard = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [coordinadores, setCoordinadores] = useState([]);
  const [allCoordinadores, setAllCoordinadores] = useState([]);
  const [selectedCoordinador, setSelectedCoordinador] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' para frontal, 'environment' para trasera
  const searchRef = useRef(null);

  useEffect(() => {
    loadStats();
    loadAllCoordinadores();
    
    // Cerrar sugerencias al hacer clic fuera
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const loadStats = async () => {
    try {
      const coordResponse = await api.get('/coordinadores/stats/general');
      const delegadosResponse = await api.get('/delegados/stats/general');

      const totalCombinado = coordResponse.data.total + delegadosResponse.data.total;
      const conFotoCombinado = coordResponse.data.conFoto + delegadosResponse.data.conFoto;
      const sinFotoCombinado = totalCombinado - conFotoCombinado;
      const porcentajeCombinado = totalCombinado > 0
        ? Math.round((conFotoCombinado / totalCombinado) * 100)
        : 0;

      setStats({
        total: totalCombinado,
        conFoto: conFotoCombinado,
        sinFoto: sinFotoCombinado,
        porcentaje: porcentajeCombinado
      });
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const loadAllCoordinadores = async () => {
    setLoading(true);
    try {
      // Cargar coordinadores
      const coordResponse = await api.get('/coordinadores', {
        params: { 
          coordinacion: user.coordinacion._id
        }
      });

      // Cargar delegados
      const delegadosResponse = await api.get('/delegados', {
        params: {
          coordinacion: user.coordinacion._id
        }
      });

      // Cargar comité seccional
      const comiteResponse = await api.get('/comite-seccional', {
        params: {
          coordinacion: user.coordinacion._id
        }
      });

      // Combinar ambos arrays, agregando un campo 'tipo' para distinguirlos
      const coordinadoresConTipo = coordResponse.data.map(c => ({ ...c, tipo: 'coordinador' }));
      const delegadosConTipo = delegadosResponse.data.map(d => ({ ...d, tipo: 'delegado' }));
      const comiteConTipo = comiteResponse.data.map(c => ({ ...c, tipo: 'comite-seccional' }));

      setAllCoordinadores([...coordinadoresConTipo, ...delegadosConTipo, ...comiteConTipo]);
    } catch (error) {
      console.error('Error al cargar coordinadores:', error);
      showMessage('error', 'Error al cargar la lista de coordinadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length >= 2) {
      const filtered = allCoordinadores.filter(coord =>
        coord.nombre.toLowerCase().includes(value.toLowerCase())
      );
      setCoordinadores(filtered);
      setShowSuggestions(true);
    } else {
      setCoordinadores([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectCoordinador = (coordinador) => {
    setSelectedCoordinador(coordinador);
    setSearchTerm(coordinador.nombre);
    setShowSuggestions(false);
    setImagePreview(null);
    setImageFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facingMode } 
      });
      setStream(mediaStream);
      setCaptureMode(true);
      
      setTimeout(() => {
        const video = document.getElementById('camera-video');
        if (video) {
          video.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      showMessage('error', 'No se pudo acceder a la cámara');
      console.error('Error al acceder a la cámara:', error);
    }
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    if (stream) {
      stopCamera();
      setTimeout(async () => {
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: newFacingMode } 
          });
          setStream(mediaStream);
          setCaptureMode(true);
          
          setTimeout(() => {
            const video = document.getElementById('camera-video');
            if (video) {
              video.srcObject = mediaStream;
            }
          }, 100);
        } catch (error) {
          showMessage('error', 'No se pudo cambiar de cámara');
          console.error('Error al cambiar de cámara:', error);
        }
      }, 100);
    }
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video');
    const canvas = document.getElementById('camera-canvas');
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'captura.jpg', { type: 'image/jpeg' });
        setImageFile(file);
        setImagePreview(canvas.toDataURL('image/jpeg'));
        stopCamera();
      }, 'image/jpeg', 0.95);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCaptureMode(false);
  };

  const handleUploadPhoto = async () => {
    if (!imageFile || !selectedCoordinador) {
      showMessage('error', 'Debes seleccionar o capturar una foto');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('foto', imageFile);
      
      // Determinar el tipo según el campo 'tipo' que agregamos al cargar
      const tipo = selectedCoordinador.tipo || 'coordinador';
      formData.append('tipo', tipo);

      await api.post(`/upload/foto/${selectedCoordinador._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showMessage('success', 'Fotografía subida correctamente');
      
      // Recargar datos
      await loadStats();
      await loadAllCoordinadores();
      
      // Limpiar formulario
      setSelectedCoordinador(null);
      setSearchTerm('');
      setImagePreview(null);
      setImageFile(null);
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al subir fotografía');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedCoordinador(null);
    setSearchTerm('');
    setImagePreview(null);
    setImageFile(null);
    stopCamera();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  return (
    <div className="container">
      <h1>Captura de Fotografías</h1>
      <p>Coordinación: <strong>{user.coordinacion?.nombre}</strong></p>

      {message.text && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'info' ? 'info' : 'error'}`}>
          {message.text}
        </div>
      )}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{stats.total}</h3>
            <p>Total Personas</p>
          </div>
          <div className="stat-card">
            <h3>{stats.conFoto}</h3>
            <p>Con Fotografía</p>
          </div>
          <div className="stat-card">
            <h3>{stats.sinFoto}</h3>
            <p>Pendientes</p>
          </div>
          <div className="stat-card">
            <h3>{stats.porcentaje}%</h3>
            <p>Progreso</p>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Buscar Persona</h2>
        
        <div className="autocomplete-container" ref={searchRef}>
          <input
            type="text"
            className="form-control search-input"
            placeholder="Escribe el nombre de la persona..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
            disabled={!!selectedCoordinador}
          />
          
          {showSuggestions && coordinadores.length > 0 && (
            <div className="suggestions-list">
              {coordinadores.map(coord => (
                <div 
                  key={coord._id} 
                  className={`suggestion-item ${coord.fotografia ? 'has-photo' : ''}`}
                  onClick={() => handleSelectCoordinador(coord)}
                >
                  <div className="suggestion-content">
                    <strong>{coord.nombre}</strong>
                    {coord.tipo === 'comite-seccional' ? (
                      <span className="suggestion-number">Función: {coord.funcion}</span>
                    ) : coord.tipo === 'delegado' ? (
                      <span className="suggestion-number">#{coord.numero} · {coord.region} · {coord.delegacionCt}</span>
                    ) : (
                      <span className="suggestion-number">#{coord.numero}</span>
                    )}
                  </div>
                  <div className="suggestion-status">
                    {coord.fotografia ? (
                      <span className="badge badge-success">✓ Con foto</span>
                    ) : (
                      <span className="badge badge-pending">Sin foto</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {showSuggestions && searchTerm.length >= 2 && coordinadores.length === 0 && (
            <div className="suggestions-list">
              <div className="suggestion-item no-results">
                No se encontraron resultados
              </div>
            </div>
          )}
        </div>

        {selectedCoordinador && !captureMode && (
          <>
            <div className="selected-person">
              <div className="selected-info">
                <h3>{selectedCoordinador.nombre}</h3>
                {selectedCoordinador.tipo === 'comite-seccional' ? (
                  <p>Función: {selectedCoordinador.funcion}</p>
                ) : selectedCoordinador.tipo === 'delegado' ? (
                  <>
                    <p>Número: {selectedCoordinador.numero}</p>
                    <p>Región: {selectedCoordinador.region}</p>
                    <p>Delegación/CT: {selectedCoordinador.delegacionCt}</p>
                  </>
                ) : (
                  <p>Número: {selectedCoordinador.numero}</p>
                )}
                {selectedCoordinador.fotografia && (
                  <span className="badge badge-success">Ya tiene fotografía</span>
                )}
              </div>
              <button 
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                ✕ Cancelar
              </button>
            </div>

            {selectedCoordinador.fotografia && (
              <div className="current-photo-section">
                <h4>Fotografía actual:</h4>
                <img 
                  src={selectedCoordinador.fotografia?.startsWith('http') 
                    ? selectedCoordinador.fotografia 
                    : `https://apisnte.apoyamos.mx/${selectedCoordinador.fotografia}`}
                  alt={selectedCoordinador.nombre}
                  className="current-photo-img"
                />
                <p className="captured-info">
                  Capturada por: <strong>{selectedCoordinador.capturadoPor?.nombre || 'N/A'}</strong>
                  {selectedCoordinador.fechaCaptura && (
                    <> el {new Date(selectedCoordinador.fechaCaptura).toLocaleDateString()}</>
                  )}
                </p>
              </div>
            )}

            {!imagePreview ? (
              <div className="capture-options">
                <h4>{selectedCoordinador.fotografia ? 'Reemplazar fotografía:' : 'Capturar fotografía:'}</h4>
                <div className="capture-buttons">
                  <label htmlFor="file-input" className="btn btn-primary btn-large">
                    📁 Seleccionar Archivo
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  
                  <button className="btn btn-success btn-large" onClick={startCamera}>
                    📷 Usar Cámara
                  </button>
                </div>
              </div>
            ) : (
              <div className="preview-section">
                <h4>Vista previa de la fotografía:</h4>
                <div className="preview-image-container">
                  <img 
                    src={imagePreview} 
                    alt="Preview"
                    className="preview-image"
                  />
                </div>
                <div className="preview-actions">
                  <button 
                    className="btn btn-success btn-large"
                    onClick={handleUploadPhoto}
                    disabled={loading}
                  >
                    {loading ? 'Subiendo...' : '✓ Confirmar y Subir'}
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => {
                      setImagePreview(null);
                      setImageFile(null);
                    }}
                  >
                    ✗ Descartar
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {captureMode && (
          <div className="camera-section">
            <h4>Capturar Fotografía</h4>
            <div className="camera-container">
              <video 
                id="camera-video" 
                autoPlay 
                playsInline
                className="camera-video"
              />
            </div>
            <canvas id="camera-canvas" style={{ display: 'none' }} />
            
            <div className="camera-actions">
              <button className="btn btn-success btn-large" onClick={capturePhoto}>
                📸 Tomar Foto
              </button>
              <button className="btn btn-primary btn-large" onClick={switchCamera}>
                🔄 Cambiar Cámara
              </button>
              <button className="btn btn-danger" onClick={stopCamera}>
                ✗ Cancelar
              </button>
            </div>
          </div>
        )}

        {!selectedCoordinador && (
          <div className="help-text">
            <p>💡 Escribe al menos 2 caracteres para buscar</p>
            <p>Las personas que ya tienen foto aparecerán marcadas con ✓</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapturistaDashboard;
